import { useCallback, useEffect, useRef } from 'react';

export default function useDebounce<T extends unknown[]>(
  callback: (...args: T) => unknown,
  delay: number,
  options: { maxWait?: number; leading?: boolean; trailing?: boolean } = {}
): Array<(...args: T) => void> {
  const maxWait = options.maxWait;
  const maxWaitHandler = useRef<any>(null);
  const maxWaitArgs = useRef<any>([]);

  const leading = options.leading;
  const trailing = options.trailing === undefined ? true : options.trailing;
  const leadingCall = useRef(false);

  const functionTimeoutHandler = useRef<any>(null);
  const isComponentUnmounted = useRef<any>(false);

  const debouncedFunction = useRef(callback);
  debouncedFunction.current = callback;

  const cancelDebouncedCallback: () => void = useCallback(() => {
    clearTimeout(functionTimeoutHandler.current);
    clearTimeout(maxWaitHandler.current);
    maxWaitHandler.current = null;
    maxWaitArgs.current = [];
    functionTimeoutHandler.current = null;
    leadingCall.current = false;
  }, []);

  useEffect(() => {
    isComponentUnmounted.current = false;
    return () => {
      isComponentUnmounted.current = true;
    };
  }, []);

  const debouncedCallback = useCallback(
    (...args: T) => {
      maxWaitArgs.current = args;
      clearTimeout(functionTimeoutHandler.current);
      if (leadingCall.current) {
        leadingCall.current = false;
      }
      if (!functionTimeoutHandler.current && leading && !leadingCall.current) {
        debouncedFunction.current(...args);
        leadingCall.current = true;
      }

      functionTimeoutHandler.current = setTimeout(() => {
        let shouldCallFunction = true;
        if (leading && leadingCall.current) {
          shouldCallFunction = false;
        }
        cancelDebouncedCallback();

        if (!isComponentUnmounted.current && trailing && shouldCallFunction) {
          debouncedFunction.current(...args);
        }
      }, delay);

      if (maxWait && !maxWaitHandler.current && trailing) {
        maxWaitHandler.current = setTimeout(() => {
          const args = maxWaitArgs.current;
          cancelDebouncedCallback();

          if (!isComponentUnmounted.current) {
            debouncedFunction.current.apply(null, args as any);
          }
        }, maxWait);
      }
    },
    [maxWait, delay, cancelDebouncedCallback, leading, trailing]
  );

  const callPending = useCallback(() => {
    if (!functionTimeoutHandler.current) {
      return;
    }

    debouncedFunction.current.apply(null, maxWaitArgs.current as any);
    cancelDebouncedCallback();
  }, [cancelDebouncedCallback]);

  return [debouncedCallback, cancelDebouncedCallback, callPending];
}
